import React, { useEffect, useState, useRef, memo, Component } from 'react';
import { FixedSizeList as List, ListChildComponentProps } from 'react-window';
import { Loan } from './types';
import { Phone, UserPlus, Clock, AlertTriangle, MapPin, DollarSign, History, Eye, CalendarClock, PhoneCall, Target, StickyNote, TrendingUp } from 'lucide-react';
import { CallOutcomeBadge } from './CallOutcomeBadge';
interface LoanQueueProps {
  loans: Loan[];
  onClaim: (id: string) => void;
  onViewLoan: (loan: Loan) => void;
  onViewTimeline: (loan: Loan) => void;
  onViewNotes: (loan: Loan) => void;
  onLogCall: (loan: Loan) => void;
  onDialOne: (loan: Loan) => void;
  claimsRemaining: number;
  dailyCap: number;
}
const ROW_HEIGHT = 104;
const LIST_HEIGHT = 720;
interface RowData {
  loans: Loan[];
  onClaim: LoanQueueProps['onClaim'];
  onViewLoan: LoanQueueProps['onViewLoan'];
  onViewTimeline: LoanQueueProps['onViewTimeline'];
  onViewNotes: LoanQueueProps['onViewNotes'];
  onLogCall: LoanQueueProps['onLogCall'];
  onDialOne: LoanQueueProps['onDialOne'];
  atCap: boolean;
}
function getUrgencyAccent(urgency: string) {
  switch (urgency) {
    case 'Critical':
      return 'border-l-red-500';
    case 'High':
      return 'border-l-orange-500';
    default:
      return 'border-l-slate-700';
  }
}
function getUrgencyBg(urgency: string) {
  switch (urgency) {
    case 'Critical':
      return 'bg-red-500/[0.04] hover:bg-red-500/[0.08]';
    case 'High':
      return 'bg-orange-500/[0.04] hover:bg-orange-500/[0.07]';
    default:
      return 'hover:bg-slate-800/30';
  }
}
const Row = memo(({
  index,
  style,
  data
}: ListChildComponentProps<RowData>) => {
  const loan = data.loans[index];
  if (!loan) return null;
  return <div style={style} className="px-1" data-id="element-271">
      <div className={`h-[96px] flex items-center gap-4 px-4 rounded-lg border border-slate-800 border-l-[3px] ${getUrgencyAccent(loan.urgency)} bg-slate-900/40 ${getUrgencyBg(loan.urgency)} transition-colors`} data-id="element-272">
        {/* Borrower & primary info */}
        <div className="flex-1 min-w-0" data-id="element-273">
          <div className="flex items-center gap-2 mb-1" data-id="element-274">
            <h3 className="text-base font-bold text-white truncate" data-id="element-275">
              {loan.borrowerName}
            </h3>
            {loan.urgency === 'Critical' && <span className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-500/20 text-red-400 text-[9px] font-bold uppercase tracking-wider border border-red-500/20 shrink-0" data-id="element-276">
                <AlertTriangle className="w-2.5 h-2.5" data-id="element-277" /> Critical
              </span>}
            <CallOutcomeBadge outcome={loan.lastCallOutcome} when={loan.lastCallAt} data-id="element-278" />
            {loan.isDialReady && <span className="hidden md:flex items-center gap-1 px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-300 text-[9px] font-bold uppercase tracking-wider border border-blue-500/20 shrink-0" data-id="element-279">
                <Phone className="w-2.5 h-2.5" data-id="element-280" /> Dial Ready
              </span>}
          </div>
          <div className="flex items-center gap-x-4 gap-y-1 text-xs text-slate-400 flex-wrap" data-id="element-281">
            <span className="flex items-center gap-1 text-white" data-id="element-282">
              <DollarSign className="w-3 h-3 text-emerald-400" data-id="element-283" />
              <span className="font-semibold" data-id="element-284">
                ${loan.amount.toLocaleString()}
              </span>
            </span>
            <span className="flex items-center gap-1" data-id="element-285">
              <MapPin className="w-3 h-3" data-id="element-286" />
              {loan.state}
            </span>
            <span className="flex items-center gap-1" data-id="element-287">
              <span className="w-1.5 h-1.5 rounded-full bg-slate-500" data-id="element-288" />
              {loan.stage}
            </span>
            <span className="flex items-center gap-1" data-id="element-289">
              <PhoneCall className="w-3 h-3" data-id="element-290" />
              {loan.callAttempts}
            </span>
          </div>
        </div>

        {/* Aging */}
        <div className="hidden md:flex items-center gap-4 text-center shrink-0" data-id="element-291">
          <div data-id="element-292">
            <div className="flex items-center justify-center gap-1 text-slate-500 mb-0.5" data-id="element-293">
              <Clock className="w-3 h-3" data-id="element-294" />
              <span className="text-[9px] font-bold uppercase tracking-wider" data-id="element-295">
                Stale
              </span>
            </div>
            <div className={`text-sm font-bold ${loan.timeSinceLastCall > 72 ? 'text-red-400' : loan.timeSinceLastCall > 36 ? 'text-orange-400' : 'text-slate-300'}`} data-id="element-296">
              {loan.timeSinceLastCall}h
            </div>
          </div>
          <div className="w-px h-6 bg-slate-800" data-id="element-297" />
          <div data-id="element-298">
            <div className="flex items-center justify-center gap-1 text-slate-500 mb-0.5" data-id="element-299">
              <History className="w-3 h-3" data-id="element-300" />
              <span className="text-[9px] font-bold uppercase tracking-wider" data-id="element-301">
                Stage
              </span>
            </div>
            <div className="text-sm font-bold text-slate-300" data-id="element-302">
              {loan.timeInStage}h
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1.5 shrink-0" data-id="element-303">
          <RowIconButton title="View" onClick={() => data.onViewLoan(loan)} icon={Eye} data-id="element-304" />
          <RowIconButton title="Timeline" onClick={() => data.onViewTimeline(loan)} icon={CalendarClock} data-id="element-305" />
          <RowIconButton title={`Notes (${loan.notes.length})`} onClick={() => data.onViewNotes(loan)} icon={StickyNote} badge={loan.notes.length || undefined} data-id="element-306" />
          <RowIconButton title="Log Call" onClick={() => data.onLogCall(loan)} icon={PhoneCall} data-id="element-307" />
          {loan.isDialReady && <RowIconButton title="Click to Dial" onClick={() => data.onDialOne(loan)} icon={Phone} accent data-id="element-308" />}
          <button onClick={() => data.onClaim(loan.id)} disabled={data.atCap} title={data.atCap ? 'Daily claim cap reached' : 'Claim this loan'} className={`ml-1 flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold transition-colors ${data.atCap ? 'bg-slate-800 text-slate-500 border border-slate-700 cursor-not-allowed' : 'bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-400 hover:to-red-500 text-white shadow-md shadow-orange-500/20'}`} data-id="element-309">
            <UserPlus className="w-3.5 h-3.5" data-id="element-310" />
            Claim
          </button>
        </div>
      </div>
    </div>;
}, areEqual);
function areEqual(prev: ListChildComponentProps<RowData>, next: ListChildComponentProps<RowData>) {
  return prev.index === next.index && prev.style.top === next.style.top && prev.data.loans === next.data.loans && prev.data.atCap === next.data.atCap;
}
function RowIconButton({
  title,
  onClick,
  icon: Icon,
  accent,
  badge
}: {
  title: string;
  onClick: () => void;
  icon: ComponentType<{
    className?: string;
  }>;
  accent?: boolean;
  badge?: number;
}) {
  return <button onClick={onClick} title={title} aria-label={title} className={`relative p-1.5 rounded-md border transition-colors ${accent ? 'bg-blue-500/15 border-blue-500/30 text-blue-300 hover:bg-blue-500/25' : 'bg-slate-800/80 border-slate-700 text-slate-400 hover:bg-slate-700 hover:text-white'}`} data-id="element-311">
      <Icon className="w-3.5 h-3.5" data-id="element-312" />
      {badge ? <span className="absolute -top-1 -right-1 min-w-[14px] h-3.5 px-1 rounded-full bg-orange-500 text-white text-[8px] font-bold flex items-center justify-center" data-id="element-313">
          {badge}
        </span> : null}
    </button>;
}
export function LoanQueue({
  loans,
  onClaim,
  onViewLoan,
  onViewTimeline,
  onViewNotes,
  onLogCall,
  onDialOne,
  claimsRemaining,
  dailyCap
}: LoanQueueProps) {
  const atCap = claimsRemaining === 0;
  const claimsUsed = dailyCap - claimsRemaining;
  const containerRef = useRef<HTMLDivElement>(null);
  const [listHeight, setListHeight] = useState(LIST_HEIGHT);
  useEffect(() => {
    const update = () => {
      // Fit within viewport, leaving room for header/filters above
      const top = containerRef.current?.getBoundingClientRect().top ?? 200;
      const next = Math.max(420, window.innerHeight - top - 40);
      setListHeight(next);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);
  // Aggregate stats for the header strip
  const totalVolume = loans.reduce((s, l) => s + l.amount, 0);
  const critical = loans.filter(l => l.urgency === 'Critical').length;
  const dialReady = loans.filter(l => l.isDialReady).length;
  const itemData: RowData = {
    loans,
    onClaim,
    onViewLoan,
    onViewTimeline,
    onViewNotes,
    onLogCall,
    onDialOne,
    atCap
  };
  return <div ref={containerRef} className="space-y-3" data-id="element-314">
      {/* Result summary + daily cap meter */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-xs text-slate-400 px-1" data-id="element-315">
        <div className="flex items-center gap-4 flex-wrap" data-id="element-316">
          <span data-id="element-317">
            <span className="font-bold text-white" data-id="element-318">
              {loans.length.toLocaleString()}
            </span>{' '}
            loan{loans.length !== 1 ? 's' : ''} in queue
          </span>
          <span className="text-slate-600" data-id="element-319">•</span>
          <span className="flex items-center gap-1" data-id="element-320">
            <TrendingUp className="w-3 h-3 text-emerald-400" data-id="element-321" />
            <span className="font-semibold text-slate-300" data-id="element-322">
              ${(totalVolume / 1000000).toFixed(1)}M
            </span>{' '}
            total volume
          </span>
          {critical > 0 && <>
              <span className="text-slate-600" data-id="element-323">•</span>
              <span className="text-red-400 font-semibold" data-id="element-324">
                {critical.toLocaleString()} critical
              </span>
            </>}
          {dialReady > 0 && <>
              <span className="text-slate-600" data-id="element-325">•</span>
              <span className="text-blue-300" data-id="element-326">
                {dialReady.toLocaleString()} dial-ready
              </span>
            </>}
        </div>

        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full border ${atCap ? 'bg-red-500/10 border-red-500/30 text-red-300' : 'bg-slate-900 border-slate-800 text-slate-300'}`} title={`Daily claim cap: ${dailyCap} per 24h`} data-id="element-327">
          <span className="font-semibold uppercase tracking-wider text-[10px]" data-id="element-328">
            Daily Claims
          </span>
          <span className="flex items-center gap-1" data-id="element-329">
            {Array.from({
            length: dailyCap
          }).map((_, i) => <span key={i} className={`h-2 w-2 rounded-full ${i < claimsUsed ? atCap ? 'bg-red-500' : 'bg-slate-500' : 'bg-emerald-500'}`} data-id="element-330" />)}
          </span>
          <span className="font-bold text-white" data-id="element-331">
            {claimsRemaining}/{dailyCap}
          </span>
          <span className="text-[10px]" data-id="element-332">left</span>
        </div>
      </div>

      {atCap && <div className="rounded-lg border border-red-500/30 bg-red-500/5 px-4 py-2.5 text-sm text-red-200 flex items-center gap-2" data-id="element-333">
          <AlertTriangle className="w-4 h-4 text-red-400 shrink-0" data-id="element-334" />
          <span data-id="element-335">
            You've reached your daily claim cap of{' '}
            <span className="font-bold" data-id="element-336">{dailyCap}</span>. Continue working your
            existing claims — the cap resets in 24 hours.
          </span>
        </div>}

      {loans.length === 0 ? <div className="text-center py-12 glass-panel rounded-xl" data-id="element-337">
          <Target className="w-12 h-12 text-slate-600 mx-auto mb-4" data-id="element-338" />
          <h3 className="text-lg font-semibold text-white mb-2" data-id="element-339">
            Queue is Clear
          </h3>
          <p className="text-slate-400" data-id="element-340">
            No loans match your current filters — try clearing them or check
            back soon.
          </p>
        </div> : <div className="rounded-xl border border-slate-800 bg-slate-950/40 overflow-hidden" data-id="element-341">
          <List height={listHeight} itemCount={loans.length} itemSize={ROW_HEIGHT} itemData={itemData} itemKey={(index, data) => data.loans[index]?.id ?? index} overscanCount={6} className="virtual-list" data-id="element-342">
            {Row}
          </List>
        </div>}
    </div>;
}